import {gql} from '@apollo/client';
import {Empty, Image, Skeleton} from 'antd';
import {useParams} from 'react-router-dom';
import {Address, SocialShare} from 'src/components';
import {usePrerenderMetaTags, useQuery, useQueryParams} from 'src/hooks';
import {itoh} from 'src/utils';
import {QueryArtById} from './__generated__/QueryArtById';
import {QueryArtByImageUri} from './__generated__/QueryArtByImageUri';
import {_QueryArtFragment} from './__generated__/_QueryArtFragment';
import hybridSvg from './hybrid.svg';
import originalSvg from './original.svg';
import styles from './Art.module.scss';

const _QUERY_ART_FRAGMENT = gql`
  fragment _QueryArtFragment on Art {
    title
    realArtist
    artist {
      name
      uri # Needed for metadata augmentation
    }
    uri # Needed for metadata augmentation
    imageUri
    isHybrid
    owner {
      id
    }
  }
`;

const QUERY_BY_ID = gql`
  query QueryArtById($id: ID!) {
    art(subgraphError: allow, id: $id) {
      ..._QueryArtFragment
    }
  }
  ${_QUERY_ART_FRAGMENT}
`;

const QUERY_BY_IMAGE_URI = gql`
  query QueryArtByImageUri($imageUri: String!) {
    arts(subgraphError: allow, where: {imageUri: $imageUri}) {
      ..._QueryArtFragment
    }
  }
  ${_QUERY_ART_FRAGMENT}
`;

function generateMetaTags(art: _QueryArtFragment) {
  return {
    'og:description': art.title,
    'og:image': itoh(art.imageUri),
  };
}

/**
 * Page for /arts/:id and /arts?imageUri=ipfs://Qm....
 */
export default function Art() {
  const {id} = useParams<{id: string}>();
  const {imageUri} = useQueryParams();

  const isQueryByImageUri = !!imageUri;
  let query, variables;
  if (isQueryByImageUri) {
    query = QUERY_BY_IMAGE_URI;
    variables = {imageUri};
  } else {
    query = QUERY_BY_ID;
    variables = {id};
  }

  const {data, loading} = useQuery<QueryArtById | QueryArtByImageUri>(query, {variables});

  let art: any;
  if (isQueryByImageUri) {
    art = (data as QueryArtByImageUri)?.arts[0];
  } else {
    art = (data as QueryArtById)?.art;
  }

  const metaTags = usePrerenderMetaTags<_QueryArtFragment>(art, generateMetaTags);

  return (
    <>
      <Skeleton loading={loading}>
        <div className={styles.container}>
          {art ? (
            <>
              <div className={styles.frame}>
                <Image
                  src={itoh(art.imageUri)}
                  alt='art'
                  fallback={process.env.PUBLIC_URL + '/missing.jpg'}
                />
              </div>
              <div className={styles.caption}>
                <div className={styles.title}>{art.title}</div>
                <div className={styles.info}>
                  {art.isHybrid ? 'Hybrid' : 'Original'}
                  <img
                    src={art.isHybrid ? hybridSvg : originalSvg}
                    alt={art.isHybrid ? 'hybrid' : 'original'}
                    title={art.isHybrid ? 'hybrid' : 'original'}
                  />
                </div>
                {!art.isHybrid && <div className={styles.info}>By: {art.realArtist}</div>}
                <div className={styles.info}>Created by: {art.artist.name}</div>
                <div className={styles.info}>
                  Owner: <Address address={art.owner.id} size='small' isLinked />
                </div>
              </div>

              <SocialShare text='Check out this artwork in the Lisa Mona NFT collection!' />
            </>
          ) : (
            <Empty description='Art not found' />
          )}
        </div>
      </Skeleton>
      {metaTags}
    </>
  );
}
